import React, { useCallback, useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "../../../axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Button from "../../../components/Button/Button";
import Modal from "../../../components/Modal/Modal";
import FormikControl from "../../../components/FormikControl/FormikControl";
import EditButton from "../../../components/EditButton/EditButton";
import DeleteButton from "../../../components/DeleteButton/DeleteButton";
import Spinner from "../../../components/Spinner/Spinner";
import DialogBox from "../../../components/DialogBox/DialogBox";

import styles from "./Index.module.css";

import AddNewIcon from "../../../assets/images/add-new-icon.svg";

const Index = (props) => {
  const [ledger, setLedger] = useState([]);
  const [formValues, setFormValues] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteDialogBox, setShowDeleteDialogBox] = useState(false);
  const [activeItem, setActiveItem] = useState(null);

  const fetchLedger = useCallback(() => {
    axios
      .get(
        `/users/${props.userId}/ledgers?broker_id=${props.broker.id}&page=1&per-page=999`
      )
      .then((response) => {
        setLedger(response.data);
      })
      .catch((error) => {
        if (error.response.data.code === 2001) {
          const errors = error.response.data.errors;
          toast.error(Object.values(errors[0])[0]);
        } else {
          toast.error(error.response.data.message);
        }
      });
  }, [props]);

  const handleNewForm = () => {
    const formValues = {
      broker_id: "",
      broker_heading_id: "",
      amount: "",
    };

    setFormValues(formValues);
    setEditMode(false);
    setShowNewForm(!showNewForm);
  };

  const handleEditForm = (id) => {
    axios
      .get(`/users/${props.userId}/ledgers/${id}`)
      .then((response) => {
        const formValues = {
          ...response.data,
        };

        setFormValues(formValues);
        setEditMode(true);
        setShowNewForm(true);
      })
      .catch((error) => {
        if (error.response.data.code === 2001) {
          const errors = error.response.data.errors;
          toast.error(Object.values(errors[0])[0]);
        } else {
          toast.error(error.response.data.message);
        }
      });
  };

  const initialValues = {
    broker_heading_id:
      formValues && formValues.heading ? formValues.heading.id : "",
    amount: formValues ? formValues.amount : "",
  };

  const validationSchema = Yup.object({
    broker_heading_id: Yup.string().required("Heading is required"),
    amount: Yup.string().required("Amount is required"),
  });

  const onSubmit = (values) => {
    setLoading(true);

    const formData = {
      broker_id: props.broker.id,
      broker_heading_id: values.broker_heading_id,
      amount: values.amount,
    };

    if (!editMode) {
      axios
        .post(`/users/${props.userId}/ledgers`, formData)
        .then((response) => {
          toast.success(response.data.message);
          fetchLedger();
          setShowNewForm(false);
        })
        .catch((error) => {
          if (error.response.data.code === 2001) {
            const errors = error.response.data.errors;
            toast.error(Object.values(errors[0])[0]);
          } else {
            toast.error(error.response.data.message);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      axios
        .put(`/users/${props.userId}/ledgers/${formValues.id}`, formData)
        .then((response) => {
          toast.success(response.data.message);
          fetchLedger();
          setShowNewForm(false);
        })
        .catch((error) => {
          if (error.response.data.code === 2001) {
            const errors = error.response.data.errors;
            toast.error(Object.values(errors[0])[0]);
          } else {
            toast.error(error.response.data.message);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  // load Broker Headings
  const loadBrokerHeadings = (inputValue) => {
    return axios
      .get(
        `/brokers-headings?broker_id=${props.broker.id}&page=1&per-page=999&keyword=${inputValue}`
      )
      .then((res) => res.data);
  };

  const newForm = showNewForm ? (
    <Modal
      show={showNewForm}
      modalClosed={handleNewForm}
      title={`${editMode ? "EDIT" : "ADD"}
        Ledger`}
    >
      <div className="FormContainer ModalForm">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ isSubmitting, setFieldValue }) => {
            return (
              <Form>
                <div className={["FormBody", styles.FormBody].join(" ")}>
                  <FormikControl
                    control="selectAsync"
                    label="Heading"
                    name="broker_heading_id"
                    value={editMode ? formValues.heading : ""}
                    setFieldValue={setFieldValue}
                    loadOptions={loadBrokerHeadings}
                  />
                  <FormikControl
                    control="input"
                    type="text"
                    label="Amount *"
                    name="amount"
                  />
                </div>
                <div className="FormActions">
                  <Button
                    btnType="Primary"
                    type="Submit"
                    disabled={isSubmitting}
                  >
                    Save
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </Modal>
  ) : null;

  const handleDelete = (id = null) => {
    setShowDeleteDialogBox(!showDeleteDialogBox);
    setActiveItem(id);
  };

  const confirmDelete = () => {
    axios
      .delete(`/users/${props.userId}/ledgers/${activeItem}`)
      .then((response) => {
        toast.success(response.data.message);
        setShowDeleteDialogBox(false);
        fetchLedger();
      })
      .catch((error) => {
        if (error.response.data.code === 2001) {
          const errors = error.response.data.errors;
          toast.error(Object.values(errors[0])[0]);
        } else {
          toast.error(error.response.data.message);
        }
      });
  };

  useEffect(() => {
    fetchLedger();
  }, [fetchLedger]);

  return (
    <div className={styles.LedgerContainer}>
      <div className={styles.Header}>
        <div className={styles.Broker}>{props.broker.name}</div>
        <div className={styles.TableActions}>
          <Button btnType="Success" clicked={handleNewForm}>
            <img src={AddNewIcon} alt="New" /> <span>Add</span>
          </Button>
        </div>
      </div>
      {ledger.length ? (
        <div className={styles.Ledger}>
          <table>
            <tbody>
              {ledger.map((item) => {
                return (
                  <tr>
                    <td>{item.heading}</td>
                    <td>Rs. {item.amount}</td>
                    <td className={styles.Actions}>
                      <EditButton clicked={() => handleEditForm(item.id)} />
                      <DeleteButton clicked={() => handleDelete(item.id)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {newForm}
      <Spinner isLoading={loading} />
      <ToastContainer />
      <DialogBox
        show={showDeleteDialogBox}
        confirm={confirmDelete}
        cancel={handleDelete}
        title="Delete Broker"
        message="Are you sure you want to delete this Broker?"
      />
    </div>
  );
};

export default Index;
